import React, { useState } from "react"

import useTitle from "@/hooks/useTitle"

import PulseLoader from "react-spinners/PulseLoader"

import {
  PlusIcon,
  MinusIcon,
  PencilSquareIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline"

import {
  FlagIcon,
  EnvelopeIcon,
  UserGroupIcon,
  BuildingOffice2Icon,
} from "@heroicons/react/24/solid"

import {
  addIcon,
  userProfileIcon2,
} from "@/assets/img/imgAssets"

import {
  useGetUsersQuery,
  useGetUserPicQuery,
} from "./usersApiSlice"

import {
  useGetUserOrgSubscriptionsQuery,
  useCreateNewUserOrgSubscriptionMutation,
  useUpdateUserOrgSubscriptionMutation,
} from "@/features/userSubscriptions/userOrgSubscriptionsApiSlice"

import {
  useGetOrgSubscriptionsQuery,
} from "@/features/orgSubscriptions/orgSubscriptionsApiSlice"

import {
  useGetUserTrailSessionPlansQuery,
  useCreateNewUserTrailSessionPlanMutation,
} from "@/features/userTrailSessions/userTrailSessionPlansApiSlice"


const EtOrgUserList = ({
  userOrgId,
  userId,
  setActiveAdminView,
  onAdd,
  onUpdate,
  onDelete,
  setSelectedEntityId,
}) => {

  // First Created - 10 May 2024
  // Author - Atul Pandey
  // Updated - 16 May 2024 - added user subscriptions
  // Updated - 30 Jul 2024 - added roleEnums
  // Updated - 31 Jul 2024 - added navigate
  // Updated - 01 Mar 2025 - updated the comp with props
  // Updated - 12 May 2025 - Added userOrgId and userId for Org based filtering
  // Updated - 06 Aug 2025 - made showOrg initial state true
  // Updated - 03 Oct 2026 - added user search
  // Updated - 03 Oct 2026 - added Active / Inactive / All user filter


  /*
  |--------------------------------------------------------------------------
  | 0 - Page Title
  |--------------------------------------------------------------------------
  */

  useTitle("Users List")


  /*
  |--------------------------------------------------------------------------
  | 1 - Users Data
  |--------------------------------------------------------------------------
  */

  const {
    data: usersData,
    isLoading,
    isError,
    error,
  } =
    useGetUsersQuery()


  /*
  |--------------------------------------------------------------------------
  | 1a - Org Subscription Data
  |--------------------------------------------------------------------------
  */

  const {
    data: orgSubscriptionsData,
  } =
    useGetOrgSubscriptionsQuery()


  /*
  |--------------------------------------------------------------------------
  | 1b - User Org Subscription Data
  |--------------------------------------------------------------------------
  */

  const {
    data: userOrgSubscriptionsData,
  } =
    useGetUserOrgSubscriptionsQuery()


  /*
  |--------------------------------------------------------------------------
  | 1c - Create User Org Subscription
  |--------------------------------------------------------------------------
  */

  const [
    createNewUserOrgSubscription,
  ] =
    useCreateNewUserOrgSubscriptionMutation()


  /*
  |--------------------------------------------------------------------------
  | 1d - Update User Org Subscription
  |--------------------------------------------------------------------------
  */

  const [
    updateUserOrgSubscription,
  ] =
    useUpdateUserOrgSubscriptionMutation()


  /*
  |--------------------------------------------------------------------------
  | 1e - Create User Trail Session Plan
  |--------------------------------------------------------------------------
  */

  const [
    createNewUserTrailSessionPlan,
  ] =
    useCreateNewUserTrailSessionPlanMutation()


  /*
  |--------------------------------------------------------------------------
  | 1f - User Trail Session Plans
  |--------------------------------------------------------------------------
  */

  const {
    data: userTrailSessionPlansData,
  } =
    useGetUserTrailSessionPlansQuery()


  /*
  |--------------------------------------------------------------------------
  | 1g - Role Enums
  |--------------------------------------------------------------------------
  */

  const rolesEnum =
    usersData?.rolesEnum


  /*
  |--------------------------------------------------------------------------
  | 2 - State
  |--------------------------------------------------------------------------
  */

  const [
    showOrg,
    setShowOrg,
  ] =
    useState({})


  const [
    selectedUser,
    setSelectedUser,
  ] =
    useState(null)


  const [
    selectedSubscriptions,
    setSelectedSubscriptions,
  ] =
    useState({})


  const [
    showSubscriptions,
    setShowSubscriptions,
  ] =
    useState({})


  const [
    showOrganizations,
    setShowOrganizations,
  ] =
    useState(true)


  const [
    showTrails,
    setShowTrails,
  ] =
    useState({})


  const [
    showUserDetails,
    setShowUserDetails,
  ] =
    useState({})


  /*
  |--------------------------------------------------------------------------
  | NEW - User Search
  |--------------------------------------------------------------------------
  */

  const [
    userSearch,
    setUserSearch,
  ] =
    useState("")


  /*
  |--------------------------------------------------------------------------
  | NEW - Active / Inactive / All Filter
  |--------------------------------------------------------------------------
  */

  const [
    userStatusFilter,
    setUserStatusFilter,
  ] =
    useState("active")


  /*
  |--------------------------------------------------------------------------
  | 2b - Extract Users
  |--------------------------------------------------------------------------
  */

  const users =
    usersData
      ?.ids
      ?.map(
        id => {

          const user =
            usersData
              ?.entities[id]


          const {
            email,
            name,
            roles,
            userOrgId,
            username,
          } =
            user


          return {
            email,
            name,
            roles,
            userOrgId,
            username,
          }
        }
      )


  /*
  |--------------------------------------------------------------------------
  | 2c - Filter Org Subscriptions
  |--------------------------------------------------------------------------
  */

  const getFilteredSubscriptions = (
    userId,
    userOrgId
  ) => {

    if (
      !orgSubscriptionsData &&
      !userOrgSubscriptionsData
    ) {
      return []
    }


    const orgSubsForUserOrg =
      orgSubscriptionsData
        ?.ids
        ?.filter(
          id =>
            orgSubscriptionsData
              ?.entities[id]
              ?.orgId
              ?._id ===
            userOrgId
              ?._id
        )


    let assignedSubs = []


    if (
      userOrgSubscriptionsData &&
      userOrgSubscriptionsData
        ?.entities
    ) {

      const userSubs =
        Object
          .values(
            userOrgSubscriptionsData
              .entities
          )
          .filter(
            userSub =>
              userSub
                .userId
                ._id
                .toString() ===
              userId
                .toString()
          )


      assignedSubs =
        userSubs.flatMap(
          userSub =>
            userSub
              .userOrgSubscriptions
              .map(
                sub =>
                  sub
                    .orgSubscription
                    ._id
                    ?.toString()
              )
        )
    }


    const filteredSubs =
      orgSubsForUserOrg
        ?.filter(
          id =>
            !assignedSubs.includes(
              id.toString()
            )
        )


    return filteredSubs
  }


  /*
  |--------------------------------------------------------------------------
  | 2d - Filter User Subscriptions
  |--------------------------------------------------------------------------
  */

  const filteredSubscriptions =
    usersData
      ?.ids
      ?.reduce(
        (
          acc,
          currentUserId
        ) => {

          const user =
            usersData
              ?.entities[
                currentUserId
              ]


          acc[
            currentUserId
          ] =
            getFilteredSubscriptions(
              currentUserId,
              user.userOrgId
            )


          return acc
        },
        {}
      ) ||
    {}


  /*
  |--------------------------------------------------------------------------
  | 2e - Get Assigned Subscriptions
  |--------------------------------------------------------------------------
  */

  const getAssignedSubscriptions = (
    currentUserId
  ) => {

    if (
      !userOrgSubscriptionsData
    ) {
      return []
    }


    const userSubs =
      Object
        .values(
          userOrgSubscriptionsData
            ?.entities ||
          {}
        )
        .filter(
          userSub =>
            userSub
              .userId
              ._id
              .toString() ===
            currentUserId
              .toString()
        )


    return userSubs.flatMap(
      userSub =>
        userSub
          .userOrgSubscriptions
    )
  }


  /*
  |--------------------------------------------------------------------------
  | 2f - Get User Trail Session Plans
  |--------------------------------------------------------------------------
  */

  const getUserTrailSessionPlans = (
    currentUserId
  ) => {

    if (
      !userTrailSessionPlansData ||
      !userTrailSessionPlansData
        ?.entities
    ) {
      return []
    }


    return Object
      .values(
        userTrailSessionPlansData
          .entities
      )
      .filter(
        plan =>
          plan
            .userId
            ._id
            .toString() ===
          currentUserId
            .toString()
      )
  }


  /*
  |--------------------------------------------------------------------------
  | 3 - Toggle Organization
  |--------------------------------------------------------------------------
  */

  const handleToggleOrg = (
    orgId
  ) => {

    setShowOrg(
      prev => ({
        ...prev,

        [orgId]:
          !prev[orgId],
      })
    )
  }


  /*
  |--------------------------------------------------------------------------
  | 3a - Select Subscription
  |--------------------------------------------------------------------------
  */

  const handleSelectSubscription = (
    currentUserId,
    subscriptionId
  ) => {

    setSelectedSubscriptions(
      prev => ({
        ...prev,

        [currentUserId]:
          subscriptionId,
      })
    )
  }


  /*
  |--------------------------------------------------------------------------
  | 3b - Toggle Subscriptions
  |--------------------------------------------------------------------------
  */

  const handleToggleSubscriptions = (
    currentUserId
  ) => {

    setShowSubscriptions(
      prev => ({
        ...prev,

        [currentUserId]:
          !prev[
            currentUserId
          ],
      })
    )
  }


  /*
  |--------------------------------------------------------------------------
  | 3c - Toggle Trails
  |--------------------------------------------------------------------------
  */

  const handleToggleTrails = (
    subscriptionId
  ) => {

    setShowTrails(
      prev => ({
        ...prev,

        [subscriptionId]:
          !prev[
            subscriptionId
          ],
      })
    )
  }


  /*
  |--------------------------------------------------------------------------
  | 3d - Toggle User Details
  |--------------------------------------------------------------------------
  */

  const handleToggleUserDetails = (
    currentUserId
  ) => {

    setShowUserDetails(
      prev => ({
        ...prev,

        [currentUserId]:
          !prev[
            currentUserId
          ],
      })
    )
  }


  /*
  |--------------------------------------------------------------------------
  | 4 - Group Users By Current Organization
  |--------------------------------------------------------------------------
  */

  const organizations = {}


  usersData
    ?.ids
    ?.filter(
      id =>
        usersData
          .entities[id]
          ?.userOrgId
          ?._id ===
        userOrgId
    )
    .forEach(
      id => {

        const user =
          usersData
            .entities[id]


        const orgId =
          user.userOrgId
            ? user
                .userOrgId
                ?._id
            : "noOrg"


        if (
          !organizations[
            orgId
          ]
        ) {

          organizations[
            orgId
          ] = {

            orgName:
              user.userOrgId
                ? user
                    .userOrgId
                    .organizationName
                : "No Org",

            orgEstablishDate:
              user.userOrgId
                ? user
                    .userOrgId
                    .organizationStartedDate
                : null,

            users: [],
          }
        }


        organizations[
          orgId
        ].users.push(
          user
        )
      }
    )


  /*
  |--------------------------------------------------------------------------
  | NEW - Current Organization Users
  |--------------------------------------------------------------------------
  */

  const currentOrgUsers =
    Object
      .values(
        organizations
      )
      .flatMap(
        organization =>
          organization
            ?.users ||
          []
      )


  /*
  |--------------------------------------------------------------------------
  | NEW - Active / Inactive / All Counts
  |--------------------------------------------------------------------------
  */

  const activeUsersCount =
    currentOrgUsers
      .filter(
        user =>
          user?.active !==
          false
      )
      .length


  const inactiveUsersCount =
    currentOrgUsers
      .filter(
        user =>
          user?.active ===
          false
      )
      .length


  const allUsersCount =
    currentOrgUsers
      .length


  /*
  |--------------------------------------------------------------------------
  | NEW - Status Filtering
  |--------------------------------------------------------------------------
  */

  const getStatusFilteredUsers = (
    orgUsers
  ) => {

    return orgUsers
      .filter(
        user => {

          if (
            userStatusFilter ===
            "all"
          ) {
            return true
          }


          if (
            userStatusFilter ===
            "inactive"
          ) {

            return (
              user?.active ===
              false
            )
          }


          return (
            user?.active !==
            false
          )
        }
      )
  }


  /*
  |--------------------------------------------------------------------------
  | NEW - Search Filtering
  |--------------------------------------------------------------------------
  */

  const normalizedUserSearch =
    userSearch
      .trim()
      .toLowerCase()


  const getVisibleUsers = (
    orgUsers
  ) => {

    const statusFilteredUsers =
      getStatusFilteredUsers(
        orgUsers
      )


    if (
      !normalizedUserSearch
    ) {
      return statusFilteredUsers
    }


    return statusFilteredUsers
      .filter(
        user => {

          const userName =
            (
              user?.name ||
              ""
            )
              .toLowerCase()


          const userEmail =
            (
              user?.email ||
              ""
            )
              .toLowerCase()


          return (
            userName.includes(
              normalizedUserSearch
            ) ||
            userEmail.includes(
              normalizedUserSearch
            )
          )
        }
      )
  }


  /*
  |--------------------------------------------------------------------------
  | 5 - Assign Subscription
  |--------------------------------------------------------------------------
  */

  const handleAssignSubscription =
    async (
      currentUserId
    ) => {

      try {

        await createNewUserOrgSubscription({
          userId:
            currentUserId,

          userOrgSubscriptions: [
            {
              orgSubscription:
                selectedSubscriptions[
                  currentUserId
                ],
            },
          ],
        }).unwrap()


        alert(
          "Subscription assigned successfully"
        )


        setSelectedUser(
          null
        )


        setSelectedSubscriptions(
          prev => ({
            ...prev,

            [currentUserId]:
              "",
          })
        )

      } catch (
        err
      ) {

        console.error(
          "Failed to assign subscription",
          err
        )


        alert(
          "Failed to assign subscription"
        )
      }
    }


  /*
  |--------------------------------------------------------------------------
  | 6 - Assign All Subscriptions
  |--------------------------------------------------------------------------
  */

  const handleAssignAllSubscriptions =
    async (
      currentUserId
    ) => {

      try {

        const remainingSubscriptions =
          orgSubscriptionsData
            ?.ids
            ?.filter(
              id =>
                !(
                  usersData
                    .entities[
                      currentUserId
                    ]
                    .userOrgSubscriptions ??
                  []
                )
                  .some(
                    sub =>
                      sub
                        .orgSubscription ===
                      id
                  ) &&
                orgSubscriptionsData
                  .entities[id]
                  .orgId
                  ?._id ===
                usersData
                  .entities[
                    currentUserId
                  ]
                  .userOrgId
                  ?._id
            )
            .map(
              id => ({
                orgSubscription:
                  id,
              })
            )


        await createNewUserOrgSubscription({
          userId:
            currentUserId,

          userOrgSubscriptions:
            remainingSubscriptions,
        }).unwrap()


        alert(
          "All subscriptions assigned successfully"
        )


        setSelectedUser(
          null
        )

      } catch (
        err
      ) {

        console.error(
          "Failed to assign all subscriptions",
          err
        )


        alert(
          "Failed to assign all subscriptions"
        )
      }
    }


  /*
  |--------------------------------------------------------------------------
  | 7 - Get User Subscriptions
  |--------------------------------------------------------------------------
  */

  const getUserSubscriptions = (
    currentUserId
  ) => {

    if (
      !userOrgSubscriptionsData
    ) {
      return []
    }


    const userSubs =
      Object
        .values(
          userOrgSubscriptionsData
            ?.entities ||
          {}
        )
        .filter(
          userSub =>
            userSub
              .userId
              ._id
              .toString() ===
            currentUserId
              .toString()
        )


    return userSubs.flatMap(
      userSub =>
        userSub
          .userOrgSubscriptions
    )
  }


  /*
  |--------------------------------------------------------------------------
  | 8 - Remove Subscription
  |--------------------------------------------------------------------------
  */

  const handleRemoveSubscription =
    async (
      currentUserId,
      subscriptionId
    ) => {

      try {

        const userSub =
          Object
            .values(
              userOrgSubscriptionsData
                ?.entities ||
              {}
            )
            .find(
              userSub =>
                userSub
                  .userId
                  ._id
                  .toString() ===
                currentUserId
                  .toString()
            )


        if (
          !userSub
        ) {
          return
        }


        const updatedSubscriptions =
          userSub
            .userOrgSubscriptions
            .filter(
              sub =>
                sub
                  .orgSubscription
                  ._id
                  .toString() !==
                subscriptionId
            )


        await updateUserOrgSubscription({
          id:
            userSub._id,

          userOrgSubscriptions:
            updatedSubscriptions,
        }).unwrap()


        alert(
          "Subscription removed successfully"
        )


        setSelectedSubscriptions(
          prev => ({
            ...prev,

            [currentUserId]:
              "",
          })
        )

      } catch (
        err
      ) {

        console.error(
          "Failed to remove subscription",
          err
        )


        alert(
          "Failed to remove subscription"
        )
      }
    }


  /*
  |--------------------------------------------------------------------------
  | 9 - Remove All Subscriptions
  |--------------------------------------------------------------------------
  */

  const handleRemoveAllSubscriptions =
    async (
      currentUserId
    ) => {

      try {

        const userSub =
          Object
            .values(
              userOrgSubscriptionsData
                ?.entities ||
              {}
            )
            .find(
              userSub =>
                userSub
                  .userId
                  ._id
                  .toString() ===
                currentUserId
                  .toString()
            )


        if (
          !userSub
        ) {
          return
        }


        await updateUserOrgSubscription({
          id:
            userSub._id,

          userOrgSubscriptions:
            [],
        }).unwrap()


        alert(
          "All subscriptions removed successfully"
        )


        setSelectedSubscriptions(
          prev => ({
            ...prev,

            [currentUserId]:
              "",
          })
        )

      } catch (
        err
      ) {

        console.error(
          "Failed to remove all subscriptions",
          err
        )


        alert(
          "Failed to remove all subscriptions"
        )
      }
    }


  /*
  |--------------------------------------------------------------------------
  | 10 - Subscription Status
  |--------------------------------------------------------------------------
  */

  const allSubscriptionsAssigned = (
    currentUserId
  ) => {

    const filteredSubs =
      filteredSubscriptions[
        currentUserId
      ] ||
      []


    return (
      filteredSubs.length ===
      0
    )
  }


  const allSubscriptionsRemoved = (
    currentUserId
  ) => {

    const userSubs =
      getUserSubscriptions(
        currentUserId
      )


    return (
      userSubs.length ===
      0
    )
  }


  /*
  |--------------------------------------------------------------------------
  | 11 - Assign Sessions
  |--------------------------------------------------------------------------
  */

  const handleAssignSessions =
    async (
      currentUserId,
      trailId
    ) => {

      try {

        await createNewUserTrailSessionPlan({
          userId:
            currentUserId,

          trailId,
        }).unwrap()


        alert(
          "Sessions assigned successfully"
        )

      } catch (
        err
      ) {

        console.error(
          "Failed to assign sessions",
          err
        )


        alert(
          "Failed to assign sessions"
        )
      }
    }


  /*
  |--------------------------------------------------------------------------
  | 12 - Sessions Assigned
  |--------------------------------------------------------------------------
  */

  const allSessionsAssigned = (
    currentUserId,
    trailId
  ) => {

    const userTrailPlans =
      getUserTrailSessionPlans(
        currentUserId
      )


    const trailSessions =
      userTrailPlans
        ?.filter(
          plan =>
            plan
              .trailId
              ._id
              .toString() ===
            trailId.toString()
        )


    return (
      trailSessions.length >
      0
    )
  }


  /*
  |--------------------------------------------------------------------------
  | Loading / Error
  |--------------------------------------------------------------------------
  */

  if (
    isLoading
  ) {

    return (
      <PulseLoader
        color="#ea580c"
      />
    )
  }


  if (
    isError
  ) {

    return (

      <div>
        Error occurred:{" "}
        {
          error
            ?.data
            ?.message
        }
      </div>
    )
  }


  /*
  |--------------------------------------------------------------------------
  | JSX
  |--------------------------------------------------------------------------
  */

  return (

    <div className="bg-gray-200 p-1 mx-auto mt-5 rounded-lg shadow-md shadow-black font-poppins">


      {/* Header */}
      <div className="m-1 p-1 flex justify-between items-center">

        <div className="flex justify-start items-center">

          <div
            className="flex justify-center items-center cursor-pointer rounded-full h-7 w-7 hover:bg-white"
            onClick={
              () =>
                setShowOrganizations(
                  prev =>
                    !prev
                )
            }
          >

            {
              showOrganizations
                ? (

                  <MinusIcon className="h-5 w-5 text-orange-600" />

                )
                : (

                  <PlusIcon className="h-5 w-5 text-orange-600" />

                )
            }

          </div>


          <h1 className="p-1 m-1 font-bold font-poppins text-lg">
            Users
          </h1>


          {/* Organization Count */}
          <div className="flex justify-start items-center m-1 p-1">

            <p className="text-[10px] text-gray-400 mx-2 px-2">
              Organizations:
            </p>


            <BuildingOffice2Icon className="h-5 w-5 text-orange-600 m-1" />


            <div className="rounded-full flex justify-center items-center h-5 min-w-5 px-1 border border-orange-300 bg-gray-50">

              <p className="text-orange-600 text-[10px]">

                {
                  Object
                    .entries(
                      organizations
                    )
                    .length
                }

              </p>

            </div>

          </div>


          {/* Total Org User Count */}
          <div className="flex justify-start items-center m-1 p-1">

            <p className="text-[10px] text-gray-400 mx-2 px-2">
              Users:
            </p>


            <UserGroupIcon className="h-5 w-5 text-orange-600 m-1" />


            <div className="rounded-full flex justify-center items-center h-5 min-w-5 px-1 border border-orange-300 bg-gray-50">

              <p className="text-orange-600 text-[10px]">

                {
                  allUsersCount
                }

              </p>

            </div>

          </div>

        </div>


        {/* Add User */}
        <div>

          <img
            src={
              addIcon
            }
            alt=""
            className="h-10 w-10 cursor-pointer"
            onClick={
              onAdd
            }
          />

        </div>

      </div>


      {
        showOrganizations &&
        Object
          .entries(
            organizations
          )
          .map(
            (
              [
                orgId,
                {
                  orgName,
                  orgEstablishDate,
                  users,
                },
              ],
              index
            ) => {

              /*
              |--------------------------------------------------------------
              | Users Visible For This Org
              |--------------------------------------------------------------
              */

              const visibleUsers =
                getVisibleUsers(
                  users
                )


              return (

                <div
                  key={
                    orgId +
                    index
                  }
                  className="bg-white m-1 p-1 rounded"
                >


                  {/* Organization Bar */}
                  <div className="bg-gray-300 m-1 p-2 rounded">


                    {/* Org Information */}
                    <div className="flex flex-wrap justify-between items-center gap-2">

                      <div className="flex justify-start items-center">

                        <div
                          className="flex justify-center items-center cursor-pointer rounded-full h-7 w-7 hover:bg-white"
                          onClick={
                            () =>
                              handleToggleOrg(
                                orgId
                              )
                          }
                        >

                          {
                            showOrg[
                              orgId
                            ]
                              ? (

                                <MinusIcon className="h-5 w-5 text-orange-600" />

                              )
                              : (

                                <PlusIcon className="h-5 w-5 text-orange-600" />

                              )
                          }

                        </div>


                        <div className="w-48">

                          <h2 className="font-bold mx-2">
                            {orgName}
                          </h2>

                        </div>


                        <div className="flex justify-start items-center">

                          <p className="text-gray-500 text-xs mx-2">
                            Established:
                          </p>


                          <FlagIcon className="h-5 w-5 text-orange-600" />


                          <p className="font-bold mx-2">
                            {
                              formatDate(
                                orgEstablishDate
                              )
                            }
                          </p>

                        </div>

                      </div>


                      {/* Active / Inactive / All */}
                      <div className="flex justify-start items-center gap-1">


                        {/* Active */}
                        <button
                          type="button"
                          onClick={
                            () =>
                              setUserStatusFilter(
                                "active"
                              )
                          }
                          className={
                            `flex justify-center items-center rounded-full border px-3 py-1 transition ${
                              userStatusFilter ===
                              "active"
                                ? "bg-orange-600 border-orange-600 text-white"
                                : "bg-white border-orange-300 text-gray-600 hover:bg-orange-50"
                            }`
                          }
                        >

                          <span className="text-[10px] font-bold">
                            Active
                          </span>


                          <span
                            className={
                              `ml-2 rounded-full min-w-5 h-5 px-1 flex justify-center items-center text-[9px] ${
                                userStatusFilter ===
                                "active"
                                  ? "bg-white text-orange-600"
                                  : "bg-orange-100 text-orange-700"
                              }`
                            }
                          >

                            {
                              activeUsersCount
                            }

                          </span>

                        </button>


                        {/* Inactive */}
                        <button
                          type="button"
                          onClick={
                            () =>
                              setUserStatusFilter(
                                "inactive"
                              )
                          }
                          className={
                            `flex justify-center items-center rounded-full border px-3 py-1 transition ${
                              userStatusFilter ===
                              "inactive"
                                ? "bg-gray-600 border-gray-600 text-white"
                                : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                            }`
                          }
                        >

                          <span className="text-[10px] font-bold">
                            Inactive
                          </span>


                          <span
                            className={
                              `ml-2 rounded-full min-w-5 h-5 px-1 flex justify-center items-center text-[9px] ${
                                userStatusFilter ===
                                "inactive"
                                  ? "bg-white text-gray-600"
                                  : "bg-gray-200 text-gray-600"
                              }`
                            }
                          >

                            {
                              inactiveUsersCount
                            }

                          </span>

                        </button>


                        {/* All */}
                        <button
                          type="button"
                          onClick={
                            () =>
                              setUserStatusFilter(
                                "all"
                              )
                          }
                          className={
                            `flex justify-center items-center rounded-full border px-3 py-1 transition ${
                              userStatusFilter ===
                              "all"
                                ? "bg-orange-100 border-orange-400 text-orange-700"
                                : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                            }`
                          }
                        >

                          <span className="text-[10px] font-bold">
                            All
                          </span>


                          <span
                            className={
                              `ml-2 rounded-full min-w-5 h-5 px-1 flex justify-center items-center text-[9px] ${
                                userStatusFilter ===
                                "all"
                                  ? "bg-orange-600 text-white"
                                  : "bg-gray-200 text-gray-600"
                              }`
                            }
                          >

                            {
                              allUsersCount
                            }

                          </span>

                        </button>

                      </div>

                    </div>


                    {/* Search Bar */}
                    <div className="mt-2 pt-2 border-t border-gray-400">

                      <div className="flex justify-between items-center gap-3">

                        <div className="flex-1 max-w-xl">

                          <div className="flex justify-start items-center bg-white border border-gray-300 rounded px-2">

                            <MagnifyingGlassIcon className="h-4 w-4 text-orange-600 mr-1" />


                            <input
                              type="text"
                              value={
                                userSearch
                              }
                              onChange={
                                e =>
                                  setUserSearch(
                                    e
                                      .target
                                      .value
                                  )
                              }
                              placeholder="Search user by name or email..."
                              className="w-full p-2 text-xs outline-none bg-transparent"
                            />


                            {
                              userSearch &&
                              (

                                <button
                                  type="button"
                                  onClick={
                                    () =>
                                      setUserSearch(
                                        ""
                                      )
                                  }
                                  className="text-[10px] text-gray-400 hover:text-orange-600 px-2"
                                >
                                  Clear
                                </button>

                              )
                            }

                          </div>

                        </div>


                        {/* Visible User Count */}
                        <div className="flex justify-start items-center">

                          <p className="text-[10px] text-gray-500 mr-2">
                            Showing:
                          </p>


                          <div className="rounded-full flex justify-center items-center h-5 min-w-5 px-2 border border-orange-300 bg-white">

                            <p className="text-orange-600 text-[10px]">

                              {
                                visibleUsers
                                  .length
                              }

                            </p>

                          </div>

                        </div>

                      </div>

                    </div>

                  </div>


                  {
                    showOrg[
                      orgId
                    ] &&
                    (

                      <div className="bg-white m-1 p-1 rounded">

                        {
                          visibleUsers
                            .length ===
                          0
                            ? (

                              <div className="m-2 p-4 rounded bg-gray-100 text-center">

                                <p className="text-xs text-gray-500">

                                  No{" "}

                                  {
                                    userStatusFilter ===
                                    "all"
                                      ? ""
                                      : `${userStatusFilter} `
                                  }

                                  users found

                                  {
                                    userSearch
                                      ? ` for "${userSearch}"`
                                      : ""
                                  }.

                                </p>

                              </div>

                            )
                            : (

                              visibleUsers
                                .map(
                                  (
                                    user,
                                    userIndex
                                  ) => (

                                    <div
                                      key={
                                        user.id +
                                        userIndex
                                      }
                                      className="bg-gray-100 rounded m-1 p-1"
                                    >


                                      {/* User Pic, Name and Email */}
                                      <div className="flex justify-between items-center">

                                        <div className="m-1 p-1 flex justify-start items-center">


                                          {/* User Toggle */}
                                          <div
                                            className="flex justify-center items-center cursor-pointer rounded-full h-7 w-7 hover:bg-white"
                                            onClick={
                                              () =>
                                                handleToggleUserDetails(
                                                  user.id
                                                )
                                            }
                                          >

                                            {
                                              showUserDetails[
                                                user.id
                                              ]
                                                ? (

                                                  <MinusIcon className="h-5 w-5 text-orange-600" />

                                                )
                                                : (

                                                  <PlusIcon className="h-5 w-5 text-orange-600" />

                                                )
                                            }

                                          </div>


                                          {/* User Pic + Name */}
                                          <div className="flex justify-start items-center m-1 p-1 w-64">

                                            <User
                                              username={
                                                user.username
                                              }
                                              defaultUserPic={
                                                userProfileIcon2
                                              }
                                            />


                                            <div>

                                              <p className="text-xs m-1 p-1">
                                                {
                                                  user.name
                                                }
                                              </p>


                                              {/* User Status */}
                                              <div className="mx-2">

                                                {
                                                  user
                                                    ?.active ===
                                                  false
                                                    ? (

                                                      <span className="text-[8px] px-2 py-[2px] rounded-full bg-gray-200 text-gray-500">
                                                        Inactive
                                                      </span>

                                                    )
                                                    : (

                                                      <span className="text-[8px] px-2 py-[2px] rounded-full bg-orange-100 text-orange-700">
                                                        Active
                                                      </span>

                                                    )
                                                }

                                              </div>

                                            </div>

                                          </div>


                                          {/* Email */}
                                          <div className="flex justify-start items-center m-1 p-1">

                                            <EnvelopeIcon className="h-5 w-5 text-orange-600" />


                                            <p className="text-xs m-1 p-1">
                                              {
                                                user.email
                                              }
                                            </p>

                                          </div>

                                        </div>


                                        {/* Update */}
                                        <div className="flex justify-start items-center mx-2">

                                          <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-white">

                                            <PencilSquareIcon
                                              onClick={
                                                () =>
                                                  onUpdate(
                                                    user?.id
                                                  )
                                              }
                                              className="h-4 w-4 text-orange-600 cursor-pointer"
                                            />

                                          </div>

                                        </div>

                                      </div>


                                      {/* 
                                        Existing Org User version intentionally
                                        has no subscription details here.
                                      */}

                                    </div>

                                  )
                                )

                            )
                        }

                      </div>

                    )
                  }

                </div>

              )
            }
          )
      }

    </div>
  )
}


/*
|--------------------------------------------------------------------------
| Format Date
|--------------------------------------------------------------------------
*/

const formatDate = (
  dateString
) => {

  if (
    !dateString
  ) {
    return "Unknown"
  }


  const date =
    new Date(
      dateString
    )


  if (
    isNaN(
      date
    )
  ) {
    return "Unknown"
  }


  return new Intl
    .DateTimeFormat(
      "en-GB",
      {
        year:
          "numeric",
      }
    )
    .format(
      date
    )
}


/*
|--------------------------------------------------------------------------
| User Pic
|--------------------------------------------------------------------------
*/

const User = ({
  username,
  defaultUserPic,
}) => {

  const {
    data: userPic,
    isFetching:
      isFetchingUserPic,
  } =
    useGetUserPicQuery(
      username
    )


  return (

    <div className="flex justify-center items-center h-12 w-12 rounded-full border border-white bg-gradient-to-r from-orange-600 to-orange-400 shadow-lg shadow-black cursor-pointer">

      {
        isFetchingUserPic
          ? (

            <div>
              Loading...
            </div>

          )
          : userPic
            ? (

              <img
                src={
                  userPic
                }
                alt="User"
                className="h-11 w-11 rounded-full"
              />

            )
            : (

              <img
                src={
                  defaultUserPic
                }
                alt="User"
                className="h-10 w-10 p-1"
              />

            )
      }

    </div>
  )
}


export default EtOrgUserList